import { ThemeEvents, CartUpdateEvent, DiscountUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import {
  fetchShippingRates,
  getShippingEstimateLabel,
  formatCep,
} from '@theme/zrzs-quick-add-shipping';
import {
  CouponController,
  hasActiveDiscountYardCoupon,
  getDiscountValueCookie,
  clearStaleDiscountValueCookie,
} from '@theme/zrzs-quick-add-discount';
import { syncGiftsOnRemove } from '@theme/zrzs-quick-add-gifts';
import { formatMoney } from '@theme/zrzs-quick-add-lens-flow';
import { CartRemoval } from '@theme/cart-remove-all';

/** Painel "carrinho", compartilhado pelo overlay mobile e pelo drawer desktop. */

/**
 * Section com o conteúdo do carrinho renderizado no servidor
 * (sections/zrzs-quick-add-cart-content.liquid → snippets/zrzs-quick-add-cart-content.liquid).
 * Pedida junto de cada mutação (`sections` no Ajax API) ou via `?section_id=`.
 */
export const QUICK_ADD_CART_SECTION_ID = 'zrzs-quick-add-cart-content';

/**
 * `_productVariantTitleValue`: armação aponta pra si mesma; kit/lente/brinde apontam pra ela (convenção de lens-selection.js).
 * @param {any} item
 */
export function selfMarker(item) {
  // /cart.js devolve variant_title null pra variante padrão; lens-selection.js
  // (e o Liquid do carrinho, via item.variant.title) gravam "Default Title".
  return `${item.variant_title || 'Default Title'} - ${item.variant_id}`;
}

/** @param {any} item */
export function isGiftItem(item) {
  return item.properties?._isGift === 'true';
}

/**
 * Brinde ganha linha própria; kit/lente nunca saem da linha da armação.
 * @param {any} item
 */
export function isAccompanyingItem(item) {
  if (isGiftItem(item)) return false;
  const props = item.properties || {};
  const marker = props._productVariantTitleValue;
  if (marker && marker !== selfMarker(item)) return true;
  return false;
}

/**
 * `_grupo` (zrzs-quick-add-cart.js) identifica itens da mesma compra —
 * `_productVariantTitleValue` não basta, pois duas compras da mesma
 * armação com lentes diferentes compartilham esse valor. Fallback pro
 * marker antigo cobre itens adicionados antes de `_grupo` existir.
 * @param {any} item
 * @param {any} other
 * @returns {boolean}
 */
export function isSameGroup(item, other) {
  const itemProps = item.properties || {};
  const otherProps = other.properties || {};
  if (itemProps._grupo) return otherProps._grupo === itemProps._grupo;
  return otherProps._productVariantTitleValue === selfMarker(item);
}

/**
 * Kit/estojo da mesma compra (mesmo _grupo), se existir — usado no grau
 * retroativo pra "mover" o kit já entregue pro grupo novo, em vez de
 * deixar updateItemGroup zerá-lo junto com a armação antiga sem repor.
 * @param {any} item
 * @param {any[]} allItems
 */
export function findKitSibling(item, allItems) {
  return (
    allItems.find(
      other =>
        other.key !== item.key && !isGiftItem(other) && isSameGroup(item, other)
    ) || null
  );
}

/** @param {string} [str] */
export function handleizeLike(str) {
  return (str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * "Pape - Óculos de Grau Tamanho P" → "Pape - grau | p". Espelha o
 * item_type de snippets/cart-products.liquid (product_type handleizado —
 * "sol" cobre solar e solar com grau, já que o tipo é da armação, não da
 * lente escolhida) + a letra de tamanho ao final do título real do
 * Shopify (sem alternative_title, que só existe no Liquid do trigger).
 * @param {any} item
 * @returns {string}
 */
export function formatCartItemDisplayName(item) {
  const title = (item.product_title || '').trim();
  if (!title) return '';
  const name = title.split('-')[0].trim();

  const productType = handleizeLike(item.product_type);
  let type = '';
  if (productType === 'oculos-de-grau') type = 'grau';
  else if (productType === 'oculos-de-sol') type = 'sol';

  const sizeMatch = title.match(/tamanho\s+([a-z])\s*$/i);
  const size = sizeMatch ? sizeMatch[1] : '';

  const nameAndType = type ? `${name} - ${type}` : name;
  return size ? `${nameAndType} | ${size}` : nameAndType;
}

/** Textos montados no navegador (snippets/zrzs-quick-add-shipping-templates.liquid). */
const DEFAULT_MESSAGES = {
  free: 'grátis',
  updateError: 'Não foi possível atualizar o carrinho. Tente novamente.',
  giftError:
    'O carrinho foi atualizado, mas não foi possível adicionar o brinde. Tente novamente.',
  shippingLoading: 'calculando frete...',
  shippingOptionsFor: 'opções de entrega para',
  shippingError: 'Não foi possível consultar o frete. Tente novamente.',
};

/** @returns {typeof DEFAULT_MESSAGES} */
function readMessages() {
  const json = document.getElementById('zqao-cart-panel-messages')?.textContent;
  if (!json) return DEFAULT_MESSAGES;
  try {
    return { ...DEFAULT_MESSAGES, ...JSON.parse(json) };
  } catch {
    return DEFAULT_MESSAGES;
  }
}

/**
 * Controla o painel "carrinho": aplica o HTML do carrinho renderizado no
 * servidor (Section Rendering API + morph), quantidade, remoção, cupom,
 * frete e o banner de grau retroativo. Não sabe nada sobre
 * o painel que a hospeda (bottom-sheet mobile ou drawer desktop) — só
 * precisa de um `root` (elemento que contém os `data-zqao-*` do carrinho)
 * e resolve o resto sozinho, igual o `connectedCallback` do overlay fazia.
 */
export class CartPanelController {
  /**
   * @param {object} options
   * @param {HTMLElement} options.root elemento que contém o markup do carrinho (data-zqao-cart-items, etc.)
   * @param {string} options.sourceId identifica esta instância nos CartUpdateEvent que ela dispara — evita que ela reaja ao próprio update (ver handleCartUpdate).
   * @param {() => boolean} [options.isVisible] se o painel está com os dados do carrinho visíveis agora — controla se um cartUpdate de outra origem deve re-renderizar. Default: sempre true.
   * @param {() => void} [options.onGrauBannerClick] clique em "escolher lentes →" no banner de grau retroativo. Mobile abre o fluxo de lentes; quem não tiver esse fluxo (desktop, por enquanto) passa um no-op.
   * @param {() => void} [options.onContinueShopping] clique em "continuar comprando" do estado vazio (dentro da região morphed — por isso delegado aqui, não um listener direto no botão). Cada host fecha do seu jeito.
   */
  constructor({
    root,
    sourceId,
    isVisible,
    onGrauBannerClick,
    onContinueShopping,
  }) {
    this.root = root;
    this.sourceId = sourceId;
    this.isVisible = isVisible || (() => true);
    this.onGrauBannerClick = onGrauBannerClick || (() => {});
    this.onContinueShopping = onContinueShopping || (() => {});

    // Linhas, banner de grau e valores do resumo vêm do servidor
    // ([data-zqao-cart-region], ver applyCartSectionHtml) e são trocados a cada
    // morph — por isso consultados na hora de usar, nunca guardados aqui.
    // Templates de frete (snippets/zrzs-quick-add-shipping-templates.liquid) — renderizados uma vez, sitewide.
    this.shippingRateTemplate = document.getElementById(
      'zqao-shipping-rate-template'
    );
    this.shippingCardTemplate = document.getElementById(
      'zqao-shipping-card-template'
    );

    this.shippingToggle = root.querySelector('[data-zqao-shipping-toggle]');
    this.shippingContent = root.querySelector('[data-zqao-shipping-content]');
    this.shippingForm = root.querySelector('[data-zqao-shipping-form]');
    this.cepInput = root.querySelector('[data-zqao-cep-input]');
    this.shippingSubmit = root.querySelector('[data-zqao-shipping-submit]');
    this.shippingResult = root.querySelector('[data-zqao-shipping-result]');

    this.couponToggle = root.querySelector('[data-zqao-coupon-toggle]');
    this.couponContent = root.querySelector('[data-zqao-coupon-content]');

    this.messages = readMessages();

    /** @type {Array<{item: any, unitIndex: number}>} candidatos elegíveis (1 entrada por unidade) do último render do carrinho. */
    this.solarGrauCandidates = [];

    /** @type {Promise<void> | null} fila de /cart/update.js — ver performCartUpdate. */
    this.cartUpdateQueue = null;
    /** @type {any[] | null} lista completa (kit/lente/brinde) do último render — usada por quem precisa achar o grupo de um item (ex. grau retroativo). */
    this.lastCartItems = null;
    /** @type {any | null} */
    this.lastCart = null;
    /**
     * Ordem das renderizações: cada pedido de HTML (GET da section ou mutação)
     * pega um número ao sair; resposta com número menor que o último aplicado
     * chegou atrasada e é descartada — senão um GET antigo, resolvido depois
     * de uma remoção, redesenharia o item removido.
     */
    this.renderRequestCount = 0;
    this.lastAppliedRenderRequest = 0;

    // Cupom (nativo + DiscountYard), no modelo do /cart — assets/zrzs-quick-add-discount.js.
    this.couponController = new CouponController({
      root,
      sourceId,
      sectionId: QUICK_ADD_CART_SECTION_ID,
      renderCartSection: html => this.renderCartSection(html),
      // DY aplicou/removeu cupom: o valor muda só no cookie do app — re-renderiza o resumo.
      onDiscountYardChange: () => {
        if (this.isVisible()) this.refreshCartPanel();
      },
    });
  }

  /** Liga os listeners e o cupom — chamar uma vez, no connectedCallback do consumidor. */
  connect() {
    // Delegação: as linhas e o banner são recriados/trocados pelo morph.
    this.root.addEventListener('click', this.handleRootClick);
    if (this.shippingToggle) {
      this.shippingToggle.addEventListener(
        'click',
        this.toggleShippingAccordion
      );
    }
    if (this.couponToggle) {
      this.couponToggle.addEventListener('click', this.toggleCouponAccordion);
    }
    if (this.shippingForm) {
      this.shippingForm.addEventListener('submit', this.handleShippingSubmit);
    }
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate
    );
    // Reage a mutação de carrinho de qualquer origem, não só a desta instância.
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);

    this.couponController.connect();
    this.readInitialCartState();
  }

  /**
   * O HTML inicial já vem do servidor com o JSON do carrinho — guarda o estado
   * na hora, pra −/+/remover funcionarem antes do primeiro refresh.
   */
  readInitialCartState() {
    const json = this.root.querySelector('[data-zqao-cart-json]')?.textContent;
    if (!json) return;
    try {
      this.storeCartState(JSON.parse(json));
    } catch {
      // JSON inválido: o primeiro refresh (ao abrir) preenche o estado.
    }
  }

  /** Desliga os listeners — chamar no disconnectedCallback do consumidor. */
  disconnect() {
    this.root.removeEventListener('click', this.handleRootClick);
    this.shippingToggle?.removeEventListener(
      'click',
      this.toggleShippingAccordion
    );
    this.couponToggle?.removeEventListener('click', this.toggleCouponAccordion);
    this.shippingForm?.removeEventListener('submit', this.handleShippingSubmit);
    document.removeEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate
    );
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    this.couponController.disconnect();
  }

  /**
   * Cupom aplicado/removido em outro lugar (outro painel, /cart). O próprio
   * painel já aplicou o HTML na mutação; fechado, busca ao abrir.
   * @param {Event} event
   */
  handleDiscountUpdate = event => {
    if (!(event instanceof DiscountUpdateEvent)) return;
    if (event.detail?.sourceId === this.sourceId) return;
    if (!this.isVisible()) return;
    this.refreshCartPanel();
  };

  /**
   * Ignora eventos disparados por esta própria instância (evita loop com
   * updateItemGroup). Com o HTML da section no evento (padrão dos
   * cart-items-component do tema), aplica direto — sem rede, mesmo fechado;
   * sem ele, busca a section só se estiver visível (fechado, busca ao abrir).
   * @param {CustomEvent<{ sourceId?: string, data?: { sections?: Record<string, string> } }>} event
   */
  handleCartUpdate = event => {
    if (event.detail && event.detail.sourceId === this.sourceId) return;
    const sectionHtml = event.detail?.data?.sections?.[QUICK_ADD_CART_SECTION_ID];
    if (!this.isVisible()) {
      // Fechado: só mantém HTML e dados em dia (o grau retroativo do desktop
      // lê lastCartItems daqui). Cupom/frete ficam pra quando abrir — abrir
      // sempre chama refreshCartPanel. Sem isso, overlay e drawer reagiriam
      // os dois ao mesmo evento (ex.: reaplicando o cupom duas vezes).
      if (sectionHtml) {
        const cart = this.applyCartSectionHtml(sectionHtml, this.startRenderRequest());
        if (cart) this.storeCartState(cart);
        this.couponController.updateNativeCouponState();
      }
      return;
    }
    if (sectionHtml) {
      this.renderCartSection(sectionHtml, this.startRenderRequest());
      return;
    }
    this.refreshCartPanel();
  };

  /**
   * Remover linha, link do banner de grau e "continuar comprando" do
   * estado vazio — delegado aqui (não listener direto no botão) porque
   * essa região é recriada a cada morph do servidor.
   * @param {MouseEvent} event
   */
  handleRootClick = event => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-zqao-grau-banner-link]')) {
      this.onGrauBannerClick();
      return;
    }

    if (target.closest('[data-zqao-cart-empty-continue]')) {
      this.onContinueShopping();
      return;
    }

    const button = target.closest('[data-zqao-remove]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const row = button.closest('[data-zqao-key]');
    const item = row
      ? (this.lastCartItems || []).find(
          cartItem => cartItem.key === row.getAttribute('data-zqao-key')
        )
      : null;
    if (!row || !item) return;

    // Trava a linha até o próximo render — evita reusar `item.quantity` desatualizada.
    row.querySelectorAll('button').forEach(rowButton => {
      rowButton.disabled = true;
    });

    this.removeCartRow(item, row.querySelector('[data-zqao-remove]'));
  };

  /**
   * Remoção com a MESMA lógica do /cart (assets/cart-remove-all.js,
   * CartRemoval): keys e "stack gift checks" calculados no Liquid
   * igual ao cart-products.liquid, brindes elegíveis e por faixa de valor
   * ressincronizados pelo mesmo código. Só a tela muda: em vez de recarregar,
   * aplica o HTML da Section Rendering API.
   * @param {any} item
   * @param {Element | null} removeButton botão da linha, com data-zqao-remove-keys
   */
  removeCartRow(item, removeButton) {
    const itemKeys = removeButton?.getAttribute('data-zqao-remove-keys') || '';
    if (!itemKeys) {
      this.updateItemGroup(item, 0);
      return;
    }

    const previous = this.cartUpdateQueue || Promise.resolve();
    const run = previous
      .catch(() => {})
      .then(async () => {
        try {
          const plan = await CartRemoval.buildPlan({
            itemKeys,
            stackGiftChecks:
              removeButton?.getAttribute('data-zqao-stack-gift-checks') || '',
          });
          /** @type {Record<string, number>} */
          const updates = {};
          plan.keysToRemove.forEach((/** @type {string} */ key) => {
            updates[key] = 0;
          });
          await this.sendCartUpdate(updates, plan.giftsToAdd);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[CartPanel] Erro ao remover item:', error);
          // Controles ficaram desabilitados — refresh reabilita.
          this.refreshCartPanel();
        }
      });
    this.cartUpdateQueue = run;
    return run;
  }

  /**
   * GET próprio em vez de sectionRenderer.getSectionHTML: aquele reaproveita um
   * GET já em andamento, que pode ter saído antes de uma mutação e trazer o
   * carrinho de antes dela.
   */
  refreshCartPanel = async () => {
    const request = this.startRenderRequest();
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', QUICK_ADD_CART_SECTION_ID);
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      await this.renderCartSection(await response.text(), request);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[CartPanel] Erro ao buscar carrinho:', error);
    }
  };

  /** Número do próximo pedido de HTML — ver renderRequestCount. */
  startRenderRequest() {
    this.renderRequestCount += 1;
    return this.renderRequestCount;
  }

  /**
   * Aplica o HTML da section e roda o que continua no cliente.
   * @param {string} html resposta da Section Rendering API (section inteira)
   * @param {number} [request] número do pedido (startRenderRequest); sem ele, conta como o mais novo
   * @returns {Promise<any | null>} o carrinho (JSON embutido na section), ou null se a resposta chegou atrasada
   */
  async renderCartSection(html, request = this.startRenderRequest()) {
    const cart = this.applyCartSectionHtml(html, request);
    if (cart) await this.afterCartRender(cart);
    return cart;
  }

  /**
   * Morph de cada [data-zqao-cart-region] da resposta na região de mesmo
   * nome deste host (overlay e drawer têm cada um a sua cópia).
   * @param {string} html
   * @param {number} request número do pedido (startRenderRequest)
   * @returns {any | null}
   */
  applyCartSectionHtml(html, request) {
    if (request < this.lastAppliedRenderRequest) return null;
    this.lastAppliedRenderRequest = request;

    const doc = new DOMParser().parseFromString(html, 'text/html');

    this.root.querySelectorAll('[data-zqao-cart-region]').forEach(region => {
      const name = region.getAttribute('data-zqao-cart-region');
      const next = doc.querySelector(`[data-zqao-cart-region="${name}"]`);
      if (next) morph(region, next);
    });

    const json = doc.querySelector('[data-zqao-cart-json]')?.textContent;
    if (!json) return null;
    try {
      return JSON.parse(json);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[CartPanel] JSON do carrinho inválido na section:', error);
      return null;
    }
  }

  /**
   * O que não sai do servidor: estado dos acordeons, cotação de frete,
   * cupom (estado só no DOM / DiscountYard) e os candidatos do grau retroativo.
   * @param {any} cart
   */
  async afterCartRender(cart) {
    const items = this.storeCartState(cart);
    const hasItems = items.some(item => !isAccompanyingItem(item));

    this.updateAccordionsAvailability(hasItems);
    // Mutação real do carrinho: uma cotação de frete calculada antes pode não
    // bater mais (peso/subtotal diferentes). Mantém o CEP digitado, só limpa
    // o resultado — o cliente clica "calcular" de novo.
    this.resetShippingResult();

    // Carrinho vazio: o valor do DiscountYard no cookie discount_value é
    // resíduo (mesma limpeza do "esvaziar carrinho" do /cart).
    if (!hasItems) clearStaleDiscountValueCookie();

    this.couponController.afterCartRender();
    this.applyDiscountYardSummary(hasItems);
  }

  /**
   * Dados do último render que a JS ainda usa: itens crus (grupo, brindes,
   * cupom, grau retroativo) e candidatos do banner de grau.
   * @param {any} cart
   * @returns {any[]} itens com quantidade
   */
  storeCartState(cart) {
    // Guarda a lista completa (kit/lente/brinde) pra achar o grupo de cada armação.
    this.lastCart = cart;
    // /cart/update.js pode ecoar de volta uma chave com quantity:0 mesmo
    // sem ser uma linha real — filtra antes de agrupar.
    const items = (cart.items || []).filter(item => item.quantity > 0);
    this.lastCartItems = items;
    this.solarGrauCandidates = this.readSolarGrauCandidates(items);
    return items;
  }

  /**
   * Armação solar SEM lente ainda, cujo produto tem opção "com grau" — a
   * elegibilidade é calculada no Liquid (data-zqao-grau-candidates: uma key
   * por UNIDADE de quantidade, o que permite configurar grau só numa parte
   * de uma linha com quantidade > 1).
   * @param {any[]} items
   * @returns {Array<{item: any, unitIndex: number}>}
   */
  readSolarGrauCandidates(items) {
    const banner = this.root.querySelector('[data-zqao-grau-banner]');
    const keys = (banner?.getAttribute('data-zqao-grau-candidates') || '')
      .split(',')
      .filter(Boolean);

    /** @type {Map<string, number>} */
    const unitsSeen = new Map();
    /** @type {Array<{item: any, unitIndex: number}>} */
    const candidates = [];
    keys.forEach(key => {
      const item = items.find(cartItem => cartItem.key === key);
      if (!item) return;
      const unitIndex = unitsSeen.get(key) || 0;
      unitsSeen.set(key, unitIndex + 1);
      candidates.push({ item, unitIndex });
    });
    return candidates;
  }

  /**
   * Subtotal/desconto/total já vêm do servidor (valores nativos do carrinho).
   * Com cupom DiscountYard ativo o desconto só existe no cookie
   * discount_value — sobrescreve os valores com esses totais.
   * @param {boolean} hasItems
   */
  applyDiscountYardSummary(hasItems) {
    const dy = hasItems ? this.getDiscountYardTotals() : null;
    if (!dy) return;

    const subtotalEl = this.root.querySelector('[data-zqao-subtotal]');
    const discountRow = this.root.querySelector('[data-zqao-discount-row]');
    const discountEl = this.root.querySelector('[data-zqao-discount]');
    const totalEl = this.root.querySelector('[data-zqao-total]');

    const discountCents = Math.round((dy.subtotal - dy.total) * 100);
    if (subtotalEl) subtotalEl.textContent = formatMoney(Math.round(dy.subtotal * 100));
    if (discountRow) discountRow.hidden = discountCents <= 0;
    if (discountEl && discountCents > 0) {
      discountEl.textContent = `-${formatMoney(discountCents)}`;
    }
    if (totalEl) totalEl.textContent = formatMoney(Math.round(dy.total * 100));
  }

  /**
   * Desconto do DiscountYard — mesma regra de assets/cart-discount-calculator.js
   * no /cart: o app grava o valor no cookie `discount_value` (reais) e o
   * desconto exibido é o maior entre esse valor e o desconto nativo do
   * carrinho. Só vale com cupom manual do DY ativo e sem cupom nativo (que
   * tira o DY de cena, como has-troca-discount no /cart).
   * @returns {{ subtotal: number, total: number } | null}
   */
  getDiscountYardTotals() {
    if (!hasActiveDiscountYardCoupon()) return null;
    if (this.couponController.hasActiveNativeCoupon()) return null;

    const discountYardReais = getDiscountValueCookie() || 0;
    if (!(discountYardReais > 0)) return null;

    const originalCents =
      this.lastCart && typeof this.lastCart.original_total_price === 'number'
        ? this.lastCart.original_total_price
        : this.lastCart?.total_price || 0;
    const subtotal = originalCents / 100;
    const nativeDiscountReais = (this.lastCart?.total_discount || 0) / 100;
    const discount = Math.max(nativeDiscountReais, discountYardReais);
    return { subtotal, total: Math.max(0, subtotal - discount) };
  }

  /**
   * Ajusta a quantidade de todo o grupo — /cart/update.js só muda quantidade, nunca properties.
   * @param {any} item
   * @param {number} newQuantity
   * @param {string[]} [groupKeys] linhas do grupo pela regra do /cart (data-zqao-group-keys);
   *   sem elas (chamadas de fora, ex. grau retroativo), usa isSameGroup.
   */
  async updateItemGroup(item, newQuantity, groupKeys) {
    const clampedQuantity = Math.max(0, newQuantity);
    const delta = clampedQuantity - item.quantity;
    const allItems = this.lastCartItems || [];

    /** @type {Record<string, number>} */
    const updates = { [item.key]: clampedQuantity };
    allItems.forEach(otherItem => {
      if (otherItem.key === item.key) return;
      const inGroup = groupKeys
        ? groupKeys.includes(otherItem.key)
        : isSameGroup(item, otherItem);
      if (inGroup) {
        updates[otherItem.key] = Math.max(0, otherItem.quantity + delta);
      }
    });

    // Remoção de verdade: brinde vinculado/por faixa de valor precisa ser ressincronizado, senão fica órfão.
    let giftsToAdd = [];
    const removingKeys = Object.keys(updates).filter(key => updates[key] === 0);
    if (removingKeys.length > 0) {
      const result = syncGiftsOnRemove({
        cartItems: allItems,
        keysToRemove: removingKeys,
        cartTotalCents: this.lastCart?.total_price || 0,
      });
      result.keysToRemove.forEach(key => {
        updates[key] = 0;
      });
      giftsToAdd = result.giftsToAdd;
    }

    return this.performCartUpdate(updates, giftsToAdd);
  }

  /**
   * Enfileira em vez de abortar — duas remoções/ajustes quase simultâneos
   * (ex. clique em 2 itens diferentes) com um AbortController compartilhado
   * cancelavam a chamada anterior mesmo sendo pra um item diferente,
   * perdendo essa remoção de verdade (bug real, ~30% das tentativas). Cada
   * chamada só sai depois da anterior terminar, nunca é cancelada.
   * @param {Record<string, number>} updates
   * @param {any[]} giftsToAdd
   */
  async performCartUpdate(updates, giftsToAdd) {
    const previous = this.cartUpdateQueue || Promise.resolve();
    const run = previous
      .catch(() => {})
      .then(() => this.sendCartUpdate(updates, giftsToAdd));
    this.cartUpdateQueue = run;
    return run;
  }

  /**
   * @param {Record<string, number>} updates
   * @param {any[]} giftsToAdd
   */
  async sendCartUpdate(updates, giftsToAdd) {
    // Padrão dos cart-items-component do tema: a própria mutação devolve o
    // HTML da section (`sections`), sem GET de /cart.js nem da section depois.
    const sectionsPayload = {
      sections: QUICK_ADD_CART_SECTION_ID,
      sections_url: window.location.pathname,
    };
    const request = this.startRenderRequest();
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, ...sectionsPayload }),
      });
      const body = await response.json();
      // 422 (ex.: "+" além do estoque): nada mudou — mostra o motivo e reabilita a linha.
      if (!response.ok) {
        this.showCartError(body.description || body.message || this.messages.updateError);
        // Carrinho não mudou: sem CartUpdateEvent, só reabilita a linha.
        await this.refreshCartPanel();
        return;
      }
      this.showCartError('');
      let { sections } = body;

      if (giftsToAdd.length > 0) {
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: giftsToAdd, ...sectionsPayload }),
        });
        // A section que vale é a de depois da adição dos brindes.
        if (addResponse.ok) {
          const added = await addResponse.json();
          if (added.sections) sections = added.sections;
        } else {
          // Quantidade principal já foi — só o brinde falhou, sem isso ficava
          // sem explicação nenhuma (carrinho certo, brinde ausente, silêncio).
          this.showCartError(this.messages.giftError);
        }
      }

      const sectionHtml = sections?.[QUICK_ADD_CART_SECTION_ID];
      let cart = null;
      if (sectionHtml) {
        cart = await this.renderCartSection(sectionHtml, request);
      } else {
        await this.refreshCartPanel();
        cart = this.lastCart;
      }

      document.dispatchEvent(
        new CartUpdateEvent(cart || {}, this.sourceId, {
          itemCount: cart?.item_count ?? 0,
          source: this.sourceId,
          sections,
        })
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[CartPanel] Erro ao atualizar quantidade:', error);
      this.showCartError(this.messages.updateError);
      // Controles ficaram desabilitados e nada vai reabilitá-los — força um refresh.
      this.refreshCartPanel();
    }
  }

  /**
   * Erro de atualização acima da lista de itens. Fica fora das regiões do
   * servidor (o morph não apaga); string vazia esconde.
   * @param {string} message
   */
  showCartError(message) {
    let errorEl = this.root.querySelector('[data-zqao-cart-error]');
    if (!errorEl) {
      if (!message) return;
      const itemsRegion = this.root.querySelector('[data-zqao-cart-region="items"]');
      if (!itemsRegion?.parentNode) return;
      errorEl = document.createElement('p');
      errorEl.className = 'zrzs-quick-add-overlay__cart-error';
      errorEl.setAttribute('data-zqao-cart-error', '');
      errorEl.setAttribute('role', 'alert');
      itemsRegion.parentNode.insertBefore(errorEl, itemsRegion);
    }
    errorEl.textContent = message;
    errorEl.hidden = !message;
  }

  toggleShippingAccordion = () => {
    if (!this.shippingToggle || !this.shippingContent) return;
    if (this.shippingToggle.disabled) return;
    const isExpanded =
      this.shippingToggle.getAttribute('aria-expanded') === 'true';
    this.shippingToggle.setAttribute('aria-expanded', String(!isExpanded));
    this.shippingContent.hidden = isExpanded;
  };

  toggleCouponAccordion = () => {
    if (!this.couponToggle || !this.couponContent) return;
    if (this.couponToggle.disabled) return;
    const isExpanded =
      this.couponToggle.getAttribute('aria-expanded') === 'true';
    this.couponToggle.setAttribute('aria-expanded', String(!isExpanded));
    this.couponContent.hidden = isExpanded;
    // O widget do DiscountYard pode ter carregado depois do último render —
    // garante que ele está na área de cupom deste painel ao abrir.
    if (!isExpanded) this.couponController.handlePanelShown();
  };

  /**
   * Sem produto no carrinho não tem frete/cupom pra calcular — desabilita
   * os dois acordeons e recolhe se algum estiver aberto.
   * @param {boolean} hasItems
   */
  updateAccordionsAvailability(hasItems) {
    if (this.shippingToggle) {
      this.shippingToggle.disabled = !hasItems;
      if (!hasItems) {
        this.shippingToggle.setAttribute('aria-expanded', 'false');
        if (this.shippingContent) this.shippingContent.hidden = true;
      }
    }
    if (this.couponToggle) {
      this.couponToggle.disabled = !hasItems;
      if (!hasItems) {
        this.couponToggle.setAttribute('aria-expanded', 'false');
        if (this.couponContent) this.couponContent.hidden = true;
      }
    }
  }

  /** @param {SubmitEvent} event */
  handleShippingSubmit = async event => {
    event.preventDefault();
    if (!this.cepInput) return;

    const cep = this.cepInput.value;

    this.renderShippingLoading();
    if (this.shippingSubmit) this.shippingSubmit.disabled = true;
    this.cepInput.disabled = true;

    try {
      const { fastest, cheapest } = await fetchShippingRates(cep);
      this.renderShippingRates(fastest, cheapest, cep);
    } catch (error) {
      this.renderShippingError(
        error instanceof Error
          ? error.message
          : this.messages.shippingError
      );
    } finally {
      if (this.shippingSubmit) this.shippingSubmit.disabled = false;
      this.cepInput.disabled = false;
    }
  };

  renderShippingLoading() {
    if (!this.shippingResult) return;
    this.shippingResult.innerHTML =
      '<p class="zrzs-quick-add-overlay__shipping-hint"></p>';
    const hint = this.shippingResult.firstElementChild;
    if (hint) hint.textContent = this.messages.shippingLoading;
  }

  /** Invalida uma cotação já exibida — carrinho mudou desde que foi calculada. */
  resetShippingResult() {
    if (this.shippingResult) this.shippingResult.innerHTML = '';
  }

  /** @param {string} message */
  renderShippingError(message) {
    if (!this.shippingResult) return;
    this.shippingResult.innerHTML = '';
    const errorEl = document.createElement('p');
    errorEl.className = 'zrzs-quick-add-overlay__shipping-error';
    errorEl.textContent = message;
    this.shippingResult.appendChild(errorEl);
  }

  /**
   * Monta o card "opções de entrega para {cep}" com a mais barata (normalmente
   * a gratuita) primeiro e, quando diferente dela, a mais rápida em seguida —
   * mesma referência em `fastest`/`cheapest` significa que só há uma opção
   * real, então só uma linha é renderizada. Sem nenhum indicador de "já
   * selecionada" — são só estimativas, a escolha final é no checkout.
   * @param {any} fastest
   * @param {any} cheapest
   * @param {string} cep
   */
  renderShippingRates(fastest, cheapest, cep) {
    if (!this.shippingResult) return;
    this.shippingResult.innerHTML = '';
    if (!this.shippingCardTemplate) return;

    const card = this.shippingCardTemplate.content.cloneNode(true);

    const titleEl = card.querySelector('[data-zqao-shipping-card-title]');
    if (titleEl) {
      titleEl.textContent = `${this.messages.shippingOptionsFor} ${formatCep(cep)}`;
    }

    const list = card.querySelector('[data-zqao-shipping-card-list]');
    this.appendShippingRateNode(list, cheapest);
    if (fastest && fastest !== cheapest) {
      this.appendShippingRateNode(list, fastest);
    }

    this.shippingResult.appendChild(card);
  }

  /**
   * @param {Element | DocumentFragment | null} container
   * @param {any} rate
   */
  appendShippingRateNode(container, rate) {
    if (!container || !this.shippingRateTemplate) return;
    const fragment = this.shippingRateTemplate.content.cloneNode(true);

    const nameEl = fragment.querySelector('[data-zqao-shipping-rate-name]');
    if (nameEl) {
      nameEl.textContent = rate.presentment_name || rate.name || 'frete';
    }

    const { estimate, price } = getShippingEstimateLabel(rate);

    const metaEl = fragment.querySelector('[data-zqao-shipping-rate-meta]');
    if (metaEl) metaEl.textContent = estimate;

    const priceEl = fragment.querySelector('[data-zqao-shipping-rate-price]');
    if (priceEl) {
      const isFree = Number(rate.price || 0) === 0;
      priceEl.textContent = isFree ? this.messages.free : price;
      priceEl.classList.toggle(
        'zrzs-quick-add-overlay__shipping-rate-price--free',
        isFree
      );
    }

    container.appendChild(fragment);
  }
}
